import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';

// Tipo para os banners
export type Banner = {
    id: string;
    image_url_desktop: string;
    image_url_mobile: string;
    title?: string;
    link_url?: string;
    is_active: boolean;
    display_order: number;
    created_at: string;
};

// Banners mockados para demonstração (fallback caso a tabela não exista)
const MOCK_BANNERS: Banner[] = [
    {
        id: '1',
        image_url_desktop: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&h=300&fit=crop&crop=center',
        image_url_mobile: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=400&fit=crop&crop=center',
        title: 'Potencialize suas vendas com Watch Ads',
        link_url: '#',
        is_active: true,
        display_order: 1,
        created_at: new Date().toISOString(),
    },
    {
        id: '2',
        image_url_desktop: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=300&fit=crop&crop=center',
        image_url_mobile: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop&crop=center',
        title: 'Nova funcionalidade: Promoções Dinâmicas',
        link_url: '#',
        is_active: true,
        display_order: 2,
        created_at: new Date().toISOString(),
    },
    {
        id: '3',
        image_url_desktop: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=300&fit=crop&crop=center',
        image_url_mobile: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop&crop=center',
        title: 'Aumente o engajamento com conteúdo visual',
        link_url: '#',
        is_active: true,
        display_order: 3,
        created_at: new Date().toISOString(),
    },
];

// Hook para buscar banners do banco de dados com realtime updates
const useBanners = () => {
    // Inicializar com banners mockados para garantir que sempre haverá algo visível
    const [banners, setBanners] = useState<Banner[]>(MOCK_BANNERS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Função para buscar banners
    const fetchBanners = useCallback(async () => {
        try {
            const { data, error: queryError } = await supabase
                .from('dashboard_banners')
                .select('*')
                .eq('is_active', true)
                .order('display_order', { ascending: true });

            if (queryError) {
                console.warn('Erro ao buscar banners, mantendo mockados:', queryError);
                return;
            }

            if (data && data.length > 0) {
                console.log('✅ Banners carregados do banco:', data.length);
                setBanners(data as Banner[]);
            } else {
                console.log('ℹ️ Nenhum banner no banco, usando mockados');
                setBanners(MOCK_BANNERS);
            }
        } catch (err) {
            console.error('Erro ao buscar banners:', err);
            setError(err instanceof Error ? err.message : 'Erro desconhecido');
        }
    }, []);

    useEffect(() => {
        // Buscar banners inicialmente
        setLoading(true);
        fetchBanners().finally(() => setLoading(false));

        // Configurar realtime subscription para detectar mudanças
        const channel = supabase
            .channel('dashboard_banners_changes')
            .on(
                'postgres_changes',
                {
                    event: '*', // INSERT, UPDATE, DELETE
                    schema: 'public',
                    table: 'dashboard_banners'
                },
                (payload) => {
                    console.log('🔄 Banner atualizado em tempo real:', payload.eventType);
                    // Recarregar todos os banners quando houver qualquer mudança
                    fetchBanners();
                }
            )
            .subscribe();

        // Cleanup: remover subscription ao desmontar
        return () => {
            supabase.removeChannel(channel);
        };
    }, [fetchBanners]);

    return { banners, loading, error, refetch: fetchBanners };
};

type BannerCarouselProps = {
    autoPlayInterval?: number;
    className?: string;
};

export const BannerCarousel = ({
    autoPlayInterval = 5000,
    className = ''
}: BannerCarouselProps) => {
    const { banners, loading } = useBanners();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const isMobile = useIsMobile();

    // Filtrar apenas banners ativos
    const activeBanners = banners.filter(b => b.is_active);

    // Auto-play
    useEffect(() => {
        if (activeBanners.length <= 1 || isHovered) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % activeBanners.length);
        }, autoPlayInterval);

        return () => clearInterval(interval);
    }, [activeBanners.length, autoPlayInterval, isHovered]);

    // Navegação
    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % activeBanners.length);
    }, [activeBanners.length]);

    const goToPrev = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + activeBanners.length) % activeBanners.length);
    }, [activeBanners.length]);

    const goToSlide = useCallback((index: number) => {
        setCurrentIndex(index);
    }, []);

    // Touch handlers para swipe em mobile
    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const minSwipeDistance = 50;

        if (Math.abs(distance) > minSwipeDistance) {
            if (distance > 0) {
                goToNext();
            } else {
                goToPrev();
            }
        }

        setTouchStart(null);
        setTouchEnd(null);
    };

    // Mostrar skeleton durante loading
    if (loading) {
        return (
            <div className={`relative w-full overflow-hidden rounded-xl shadow-lg bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 animate-pulse ${className}`}>
                <div className={`w-full ${isMobile ? 'h-48 sm:h-56' : 'h-40 md:h-48 lg:h-56'}`} />
            </div>
        );
    }

    // Não renderizar se não houver banners
    if (activeBanners.length === 0) {
        return null;
    }

    const currentBanner = activeBanners[currentIndex];
    const imageUrl = isMobile ? currentBanner.image_url_mobile : currentBanner.image_url_desktop;

    return (
        <div
            className={`relative w-full overflow-hidden rounded-xl shadow-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-900 ${className}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Container do Banner - Altura responsiva */}
            <div className={`relative w-full ${isMobile ? 'h-48 sm:h-56' : 'h-40 md:h-48 lg:h-56'}`}>
                {/* Imagem do Banner */}
                <a
                    href={currentBanner.link_url || '#'}
                    target={currentBanner.link_url?.startsWith('http') ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                >
                    <div
                        className="absolute inset-0 bg-cover bg-center transition-opacity duration-500 ease-in-out"
                        style={{ backgroundImage: `url(${imageUrl})` }}
                    >
                        {/* Overlay gradiente para melhor legibilidade */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                        {/* Título do Banner */}
                        {currentBanner.title && (
                            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                                <h3 className="text-white font-bold text-lg md:text-xl lg:text-2xl drop-shadow-lg">
                                    {currentBanner.title}
                                </h3>
                            </div>
                        )}
                    </div>
                </a>

                {/* Botões de navegação - Visíveis em hover ou sempre em mobile */}
                {activeBanners.length > 1 && (
                    <>
                        <button
                            onClick={(e) => { e.preventDefault(); goToPrev(); }}
                            className={`absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-white hover:scale-110 ${isHovered || isMobile ? 'opacity-100' : 'opacity-0'}`}
                            aria-label="Banner anterior"
                        >
                            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-white" />
                        </button>

                        <button
                            onClick={(e) => { e.preventDefault(); goToNext(); }}
                            className={`absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-white hover:scale-110 ${isHovered || isMobile ? 'opacity-100' : 'opacity-0'}`}
                            aria-label="Próximo banner"
                        >
                            <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-white" />
                        </button>
                    </>
                )}

                {/* Indicadores de slide */}
                {activeBanners.length > 1 && (
                    <div className="absolute bottom-2 md:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                        {activeBanners.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                className={`transition-all duration-300 rounded-full ${index === currentIndex
                                    ? 'w-6 md:w-8 h-2 bg-white'
                                    : 'w-2 h-2 bg-white/50 hover:bg-white/75'
                                    }`}
                                aria-label={`Ir para banner ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Badge indicando que é promocional */}
            <div className="absolute top-2 md:top-3 right-2 md:right-3">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500 text-white shadow-md">
                    ✨ Novidades
                </span>
            </div>
        </div>
    );
};

export default BannerCarousel;
